import { useIsFocused } from '@react-navigation/native'
import { useGlobalSearchParams } from 'expo-router'
import { pick } from 'lodash'
import { FC, Suspense, useEffect, useRef, type ReactNode } from 'react'
import { type FetchPolicy } from 'relay-runtime'

import { LoadingView } from '@/components'
import {
  consumeRelayPreloadedQuery,
  getRelayPreloadKey,
  useQueryLoaderByName,
  type RelayQueryName,
  type RelayQueryVariablesByName,
  type ScreenLoaderComponentProps
} from '@/lib'

type OtherProps = Record<string, unknown>

// type ScreenComponentProps<N extends RelayQueryName> = { queryRef: RelayPreloadedQueryByName<N> }
type ScreenLoaderWithQueryProps<N extends RelayQueryName> = {
  fetchPolicy?: FetchPolicy
  paramKeys?: string[]
  queryName: N
  ScreenComponent: FC<ScreenLoaderComponentProps<N>> | ((props: ScreenLoaderComponentProps<N>) => ReactNode)
}

export const ScreenLoaderWithQuery = <N extends RelayQueryName>({
  fetchPolicy = 'store-or-network',
  paramKeys = [],
  queryName,
  ScreenComponent
}: ScreenLoaderWithQueryProps<N> & OtherProps) => {
  const params = useGlobalSearchParams()

  // we are doing these as refs, so they don't trigger re-renders while loading
  const queryVariables = useRef<RelayQueryVariablesByName<N>>(paramKeys.length ? pick(params, paramKeys) : {})

  // do we have a preloaded query in the store?
  const preloadedKey = useRef(getRelayPreloadKey(queryName, queryVariables.current))
  const preloadedRef = useRef(consumeRelayPreloadedQuery(queryName, queryVariables.current))
  const isPreload = useRef(!!preloadedRef)

  // initialize the query, using the preloadedQuery if it exists
  const [loadedQuery, loadQuery, disposeQuery] = useQueryLoaderByName(queryName, preloadedRef.current)

  useEffect(() => {
    if (!loadedQuery) {
      // if the query isn't loaded yet in any way (like if this screen was force-reloaded or was deep-linked), load it
      loadQuery(queryVariables.current, { fetchPolicy })

      if (__DEV__) {
        console.debug(`[relay] loading query ${preloadedKey.current} ${isPreload.current ? '(preloaded)' : ''}`)
      }
    } else {
      // capturing ref value, in case it changes before callback is fired
      const key = preloadedKey.current

      // query is loaded, so we add an unmount callback to dispose of it
      return () => {
        disposeQuery()

        if (__DEV__) {
          console.debug(`[relay] disposed query ${key}`)
        }
      }
    }
  }, [disposeQuery, fetchPolicy, loadQuery, loadedQuery, queryName])

  if (!loadedQuery) {
    // show loading state when the query isn't loaded yet
    return <LoadingView />
  }

  if (ScreenComponent) {
    return <ScreenComponent queryRef={loadedQuery} />
  }

  return null
}

type ScreenLoaderWithoutQueryProps = {
  ScreenComponent?: FC | ((props: any) => ReactNode)
}

export const ScreenLoader = <N extends RelayQueryName>(
  props: ScreenLoaderWithoutQueryProps | ScreenLoaderWithQueryProps<N>
) => {
  const isFocused = useIsFocused()

  return isFocused ? (
    <Suspense fallback={<LoadingView />}>
      {'queryName' in props ? (
        <ScreenLoaderWithQuery {...props} />
      ) : props.ScreenComponent ? (
        <props.ScreenComponent />
      ) : null}
    </Suspense>
  ) : null
}
